/*
 * Akomoda — per-hotel Billing panel
 * -----------------------------------------------------------------
 * Mounted inside <SuperHotelDetail /> as a tab. Contains:
 *   · Subscription summary + start / rooms-override
 *   · Modules grid (grouped by category) with enable / disable
 *   · Invoices list + generate current-period invoice
 *   · Discounts & credits
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import {
    Plus, CheckCircle2, XCircle, Play, DollarSign, Package,
    Sparkles, FileText, Percent, Coins, AlertTriangle, Calculator,
    Lock, Unlock, Edit3, Trash2,
} from "lucide-react";

const money = (v, cur = "EUR") =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format(Number(v || 0));

export default function HotelBillingTab({ hotelId }) {
    const [subData, setSubData] = useState(null);
    const [mods, setMods] = useState([]);
    const [cats, setCats] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [credits, setCredits] = useState([]);
    const [openModule, setOpenModule] = useState(null);
    const [showAllModules, setShowAllModules] = useState(false);
    const [modFilter, setModFilter] = useState("");

    const load = async () => {
        try {
            const [s, m, c, i, d, cr] = await Promise.all([
                api.get(`/saas/hotels/${hotelId}/subscription`),
                api.get(`/saas/hotels/${hotelId}/modules`),
                api.get("/saas/categories"),
                api.get(`/saas/invoices?hotel_id=${hotelId}`),
                api.get(`/saas/discounts?hotel_id=${hotelId}`),
                api.get(`/saas/credits?hotel_id=${hotelId}`),
            ]);
            setSubData(s.data); setMods(m.data); setCats(c.data);
            setInvoices(i.data); setDiscounts(d.data); setCredits(cr.data);
        } catch (e) { toast.error("Failed to load billing data"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [hotelId]);

    const modsByCat = useMemo(() => {
        const q = modFilter.trim().toLowerCase();
        const g = {};
        mods.forEach((m) => {
            // Default: hide draft / deprecated / disabled from the customer view.
            if (!showAllModules && ["draft", "deprecated", "disabled"].includes(m.status)) return;
            if (q && !(m.name.toLowerCase().includes(q) || m.key.toLowerCase().includes(q))) return;
            (g[m.category] = g[m.category] || []).push(m);
        });
        return g;
    }, [mods, showAllModules, modFilter]);

    if (!subData) return <div className="akm-card animate-pulse p-8" style={{ minHeight: 300 }} />;
    const sub = subData.subscription;

    return (
        <div className="space-y-6" data-testid="hotel-billing-tab">
            {/* ── Subscription card ─────────────────────────────────── */}
            <div className="akm-card p-4">
                <div className="mb-3 flex items-baseline justify-between">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                        <DollarSign size={14} /> Subscription
                    </h2>
                    {!sub && (
                        <StartSubscriptionButton hotelId={hotelId} onDone={load} autoRooms={subData.auto_billable_room_count} />
                    )}
                </div>
                {!sub ? (
                    <div className="rounded-md border border-dashed border-border p-6 text-center">
                        <AlertTriangle size={20} className="mx-auto mb-2 text-amber-500" />
                        <div className="text-[13px] font-semibold">No active subscription</div>
                        <div className="text-[11px] text-muted-foreground">
                            Auto-detected billable rooms: <b>{subData.auto_billable_room_count}</b>
                        </div>
                    </div>
                ) : (
                    <SubscriptionSummary sub={sub} autoRooms={subData.auto_billable_room_count} hotelId={hotelId} onChange={load} />
                )}
            </div>

            {/* ── Modules ─────────────────────────────────────────── */}
            <div className="akm-card p-4">
                <div className="mb-3 flex flex-wrap items-baseline gap-2">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                        <Package size={14} /> Modules & Billing
                    </h2>
                    <input placeholder="Search modules…" value={modFilter}
                           onChange={(e) => setModFilter(e.target.value)}
                           className="akm-input ml-auto w-56 text-[11px]"
                           data-testid="module-search" />
                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <input type="checkbox" checked={showAllModules}
                               onChange={(e) => setShowAllModules(e.target.checked)}
                               data-testid="show-all-modules" />
                        Show drafts & deprecated
                    </label>
                </div>
                {cats.map((c) => {
                    const items = modsByCat[c.key] || [];
                    if (!items.length) return null;
                    return (
                        <div key={c.key} className="mb-4">
                            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{c.name}</h3>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                {items.map((m) => (
                                    <ModuleRow key={m.key} m={m} hotelId={hotelId} onChange={load} onOpen={() => setOpenModule(m)} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Invoices ────────────────────────────────────────── */}
            <div className="akm-card p-4">
                <div className="mb-3 flex items-baseline justify-between">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                        <FileText size={14} /> Invoices
                    </h2>
                    {sub && (
                        <button className="akm-btn-primary text-[11px]" onClick={async () => {
                            const isTrial = sub?.status === "trial";
                            const isCustomQuote = sub?.commercial_mode === "custom_quote" && !sub?.custom_quote_approved;
                            let force = false;
                            if (isTrial) {
                                if (!window.confirm(
                                    `Subscription is still in TRIAL until ${sub.trial_ends_at?.slice(0, 10)}.\n\n` +
                                    `Generating an invoice now will override the trial. Continue?`,
                                )) return;
                                force = true;
                            }
                            if (isCustomQuote) {
                                toast.error("Custom Quote not approved. Set contracted price first.");
                                return;
                            }
                            try {
                                const { data } = await api.post(
                                    `/saas/invoices/generate/${hotelId}${force ? "?force=true" : ""}`,
                                );
                                if (data.idempotent_hit) {
                                    toast.info(`Already generated — ${data.invoice_number}`);
                                } else {
                                    toast.success(`Generated ${data.invoice_number}`);
                                }
                                load();
                            } catch (e) {
                                const detail = e.response?.data?.detail;
                                const msg = typeof detail === "object" ? (detail?.message || detail?.error) : detail;
                                toast.error(msg || "Failed to generate invoice");
                            }
                        }} data-testid="generate-invoice">
                            <Plus size={12} /> Generate Current Period
                        </button>
                    )}
                </div>
                <table className="w-full text-[12px]">
                    <thead className="text-left text-muted-foreground">
                        <tr><th className="pb-2">#</th><th>Status</th><th>Total</th><th>Paid</th><th>Due</th><th>Created</th><th></th></tr>
                    </thead>
                    <tbody>
                        {invoices.map((i) => (
                            <tr key={i.id} className="border-t border-border">
                                <td className="py-2 font-mono">{i.invoice_number}</td>
                                <td><span className={"rounded-full px-2 py-0.5 text-[10px] uppercase " + (i.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>{i.status}</span></td>
                                <td className="font-bold">{money(i.total, i.currency)}</td>
                                <td className="text-emerald-700">{money(i.amount_paid, i.currency)}</td>
                                <td className="text-red-700">{money(i.amount_due, i.currency)}</td>
                                <td className="text-[10px]">{new Date(i.created_at).toLocaleDateString("en-GB")}</td>
                                <td>
                                    {i.status !== "paid" && (
                                        <button className="akm-btn-ghost text-[10px] text-emerald-600" onClick={async () => {
                                            await api.post(`/saas/invoices/${i.id}/mark-paid`, { amount: i.amount_due, method: "manual" });
                                            toast.success("Paid"); load();
                                        }}>Mark Paid</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {invoices.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No invoices yet</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* ── Discounts + Credits ─────────────────────────────── */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DiscountsCard hotelId={hotelId} rows={discounts} onChange={load} />
                <CreditsCard hotelId={hotelId} rows={credits} onChange={load} />
            </div>

            {openModule && <ModuleActivationPanel module={openModule} hotelId={hotelId} onClose={() => setOpenModule(null)} onSaved={() => { setOpenModule(null); load(); }} />}
        </div>
    );
}

// ─── Subscription summary + inline controls ─────────────────────
function SubscriptionSummary({ sub, autoRooms, hotelId, onChange }) {
    const [overrideOpen, setOverrideOpen] = useState(false);
    const isTrial = sub.status === "trial";
    return (
        <div className="space-y-3">
            {isTrial && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="trial-banner">
                    <div className="flex items-baseline gap-2">
                        <Sparkles size={14} className="text-blue-700" />
                        <div className="text-[13px] font-semibold text-blue-900">Free trial in progress</div>
                    </div>
                    <div className="mt-1 grid grid-cols-1 gap-1 text-[12px] text-blue-900 md:grid-cols-3">
                        <div>Trial ends: <b>{sub.trial_ends_at ? formatDate(sub.trial_ends_at) : "—"}</b></div>
                        <div>Estimated recurring after trial: <b>{money(sub.recurring_total, sub.currency)} / {sub.billing_cycle === "annual" ? "year" : "month"}</b></div>
                        <div>No charge during trial</div>
                    </div>
                </div>
            )}
            {sub.commercial_mode === "custom_quote" && !sub.custom_quote_approved && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="text-[13px] font-semibold text-amber-900 flex items-center gap-2">
                        <AlertTriangle size={14} /> Custom quote required
                    </div>
                    <div className="mt-1 text-[12px] text-amber-900">
                        This room count falls in the 151+ Custom Quote tier. Please approve
                        a contracted price before invoicing this hotel.
                    </div>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <SumKV label="Status" val={<span className={"rounded-full px-2 py-0.5 text-[10px] uppercase " + (sub.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800")}>{sub.status}</span>} />
                <SumKV label="Billing cycle" val={sub.billing_cycle} />
                <SumKV label="Currency" val={sub.currency} />
                <SumKV label="Trial ends" val={sub.trial_ends_at ? formatDate(sub.trial_ends_at) : "—"} />
                <SumKV label="Billable rooms" val={<>
                    <b>{sub.billable_room_count}</b>
                    <span className="ml-1 text-[10px] text-muted-foreground">({sub.room_count_source})</span>
                    <button className="akm-btn-ghost ml-2 text-[10px]" onClick={() => setOverrideOpen(!overrideOpen)}>Override</button>
                </>} />
                <SumKV label="Auto-detected" val={autoRooms} />
                <SumKV label="Grace period" val={`${sub.grace_period_days || 7}d`} />
                <SumKV label="Next billing" val={sub.next_billing_at ? formatDate(sub.next_billing_at) : "—"} />
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <SumKV label="Platform charge" val={<b>{money(sub.platform_charge, sub.currency)}</b>} />
                <SumKV label="Modules total" val={money(sub.module_total, sub.currency)} />
                <SumKV label="Discounts" val={"-" + money(sub.discount_total, sub.currency)} />
                <SumKV label="Recurring total" val={<b className="text-primary">{money(sub.recurring_total, sub.currency)}</b>} />
            </div>
            {sub.commercial_mode && sub.commercial_mode !== "automatic" && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px]">
                    <b>Custom Quote required:</b> This room count triggers the custom_quote tier. Super Admin has assigned the fallback rate.
                </div>
            )}
            {overrideOpen && (
                <RoomOverrideForm hotelId={hotelId} current={sub.billable_room_count} onCancel={() => setOverrideOpen(false)} onDone={() => { setOverrideOpen(false); onChange(); }} />
            )}
        </div>
    );
}
const SumKV = ({ label, val }) => (
    <div>
        <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
        <div className="text-[13px]">{val}</div>
    </div>
);

function RoomOverrideForm({ hotelId, current, onCancel, onDone }) {
    const [rooms, setRooms] = useState(current);
    const [reason, setReason] = useState("");
    const save = async () => {
        if (!reason.trim()) return toast.error("Reason required");
        await api.post(`/saas/hotels/${hotelId}/subscription/rooms-override`, {
            room_count: Number(rooms), reason,
        });
        toast.success("Override saved"); onDone();
    };
    return (
        <div className="rounded-md border border-dashed border-border p-3">
            <div className="mb-2 text-[11px] font-semibold">Override billable room count</div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                <input type="number" className="akm-input" value={rooms} onChange={(e) => setRooms(e.target.value)} data-testid="override-rooms" />
                <input className="akm-input md:col-span-2" placeholder="Reason (audit log)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-reason" />
            </div>
            <div className="mt-2 flex justify-end gap-2">
                <button className="akm-btn-ghost text-[11px]" onClick={onCancel}>Cancel</button>
                <button className="akm-btn-primary text-[11px]" onClick={save} data-testid="override-save">Save Override</button>
            </div>
        </div>
    );
}

function StartSubscriptionButton({ hotelId, autoRooms, onDone }) {
    const [open, setOpen] = useState(false);
    const [cycle, setCycle] = useState("monthly");
    const [trial, setTrial] = useState(14);
    const start = async () => {
        await api.post(`/saas/hotels/${hotelId}/subscription`, { billing_cycle: cycle, trial_days: Number(trial) });
        toast.success("Subscription started"); setOpen(false); onDone();
    };
    if (!open) return (
        <button className="akm-btn-primary text-[11px]" onClick={() => setOpen(true)} data-testid="start-subscription">
            <Play size={12} /> Start Subscription
        </button>
    );
    return (
        <div className="flex items-end gap-2">
            <div>
                <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Cycle</label>
                <select className="akm-input text-[11px]" value={cycle} onChange={(e) => setCycle(e.target.value)}>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                </select>
            </div>
            <div>
                <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Trial (days)</label>
                <input type="number" className="akm-input w-20 text-[11px]" value={trial} onChange={(e) => setTrial(e.target.value)} />
            </div>
            <div className="text-[10px] text-muted-foreground">
                Auto rooms: <b>{autoRooms}</b>
            </div>
            <button className="akm-btn-primary text-[11px]" onClick={start} data-testid="start-subscription-confirm">Start</button>
        </div>
    );
}

// ─── One module row (per-hotel modules grid) ────────────────────
function ModuleRow({ m, hotelId, onChange, onOpen }) {
    const ent = m.entitlement;
    const enabled = ent && ["enabled", "trial"].includes(ent.status);
    const toggle = async () => {
        if (enabled) {
            if (!confirm(`Disable ${m.name}?`)) return;
            await api.delete(`/saas/hotels/${hotelId}/modules/${m.key}?mode=immediate&reason=Super+Admin+manual+disable`);
            toast.success("Disabled"); onChange();
        } else {
            onOpen();
        }
    };
    return (
        <div className={"flex items-center gap-3 rounded-md border p-2.5 " + (enabled ? "border-emerald-200 bg-emerald-50/40" : m.is_core ? "border-slate-200 bg-slate-50/40" : "border-border")}
             data-testid={`module-row-${m.key}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                    <div className="text-[12px] font-medium">{m.name}</div>
                    {m.is_core && <span className="rounded-full bg-slate-200 px-1.5 py-0 text-[9px] font-semibold uppercase">Core</span>}
                    {ent && <span className={"rounded-full px-1.5 py-0 text-[9px] font-semibold uppercase " +
                        (enabled ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800")}>{ent.status}</span>}
                </div>
                <div className="text-[10px] text-muted-foreground">
                    {m.billing_type} · {Number(m.default_monthly_price) > 0 ? money(m.default_monthly_price) + "/mo" : "included"}
                    {m.item?.contracted_unit_price && <> · <b>contracted {money(m.item.contracted_unit_price)}</b></>}
                </div>
            </div>
            {!m.is_core && (
                <button className={enabled ? "akm-btn-ghost text-[10px] text-red-600" : "akm-btn-primary text-[10px]"}
                        onClick={toggle} data-testid={`module-toggle-${m.key}`}>
                    {enabled ? <><Lock size={10} /> Disable</> : <><Unlock size={10} /> Enable</>}
                </button>
            )}
        </div>
    );
}

// ─── Module activation panel (side drawer) ──────────────────────
function ModuleActivationPanel({ module, hotelId, onClose, onSaved }) {
    const [f, setF] = useState({
        access_source: "paid_addon",
        billing_cycle: "monthly",
        contracted_monthly_price: module.default_monthly_price,
        contracted_annual_price: module.default_annual_price,
        currency: "EUR",
        quantity: 1,
        starts_at: "",
        ends_at: "",
        trial_ends_at: "",
        proration_mode: "start_next_cycle",
        discount: "0",
        internal_reason: "",
        invoice_description: module.name,
        status: "enabled",
    });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
            await api.post(`/saas/hotels/${hotelId}/modules/${module.key}`, payload);
            toast.success(`${module.name} enabled`); onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d?.error === "dependencies_missing") {
                toast.error(`Missing: ${d.missing.join(", ")}`);
            } else {
                toast.error(typeof d === "string" ? d : "Failed");
            }
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
            <div className="absolute right-0 top-0 h-full w-full max-w-lg overflow-y-auto bg-background shadow-xl"
                 onClick={(e) => e.stopPropagation()} data-testid="activation-panel">
                <div className="border-b border-border p-4">
                    <div className="text-lg font-bold">{module.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{module.key}</div>
                    <p className="mt-1 text-[11px] text-muted-foreground">{module.description}</p>
                </div>
                <div className="space-y-3 p-4">
                    <Field label="Access source">
                        <select className="akm-input" value={f.access_source} onChange={(e) => setF({ ...f, access_source: e.target.value })} data-testid="ap-source">
                            <option value="paid_addon">Paid add-on</option>
                            <option value="free_override">Free override</option>
                            <option value="trial">Trial</option>
                            <option value="custom_agreement">Custom agreement</option>
                            <option value="plan">Included in plan</option>
                        </select>
                    </Field>
                    <Field label="Billing cycle">
                        <select className="akm-input" value={f.billing_cycle} onChange={(e) => setF({ ...f, billing_cycle: e.target.value })}>
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Contracted monthly (€)">
                            <input className="akm-input" value={f.contracted_monthly_price} onChange={(e) => setF({ ...f, contracted_monthly_price: e.target.value })} data-testid="ap-monthly" />
                        </Field>
                        <Field label="Contracted annual (€)">
                            <input className="akm-input" value={f.contracted_annual_price} onChange={(e) => setF({ ...f, contracted_annual_price: e.target.value })} />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Quantity">
                            <input type="number" className="akm-input" value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} />
                        </Field>
                        <Field label="Proration">
                            <select className="akm-input" value={f.proration_mode} onChange={(e) => setF({ ...f, proration_mode: e.target.value })}>
                                <option value="start_next_cycle">Start next cycle</option>
                                <option value="charge_prorated_now">Charge prorated now</option>
                                <option value="add_to_next_invoice">Add prorated to next invoice</option>
                                <option value="charge_full">Charge full amount</option>
                                <option value="waive">Waive current period</option>
                            </select>
                        </Field>
                    </div>
                    <Field label="Invoice description">
                        <input className="akm-input" value={f.invoice_description} onChange={(e) => setF({ ...f, invoice_description: e.target.value })} />
                    </Field>
                    <Field label="Internal reason (audit log)">
                        <textarea className="akm-input" rows={2} value={f.internal_reason} onChange={(e) => setF({ ...f, internal_reason: e.target.value })} data-testid="ap-reason" />
                    </Field>
                    {module.dependencies?.length > 0 && (
                        <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-[10px] text-blue-800">
                            Requires: <b>{module.dependencies.join(", ")}</b>
                        </div>
                    )}
                </div>
                <div className="sticky bottom-0 flex justify-end gap-2 border-t border-border bg-background p-3">
                    <button className="akm-btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="akm-btn-primary" onClick={save} disabled={busy} data-testid="ap-save">
                        {busy ? "Enabling…" : <>Enable Module</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
const Field = ({ label, children }) => (
    <div>
        <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{label}</label>
        {children}
    </div>
);

// ─── Discounts + Credits mini-cards ─────────────────────────────
function DiscountsCard({ hotelId, rows, onChange }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const add = async () => {
        if (!amount || !reason) return toast.error("Amount + reason required");
        await api.post("/saas/discounts", { hotel_id: hotelId, type: "fixed_amount", amount, reason });
        toast.success("Discount added"); setAmount(""); setReason(""); setOpen(false); onChange();
    };
    return (
        <div className="akm-card p-4">
            <div className="mb-3 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2"><Percent size={13} /> Discounts</h3>
                <button className="akm-btn-primary text-[10px]" onClick={() => setOpen(!open)}>{open ? "Cancel" : <><Plus size={10} /> Add</>}</button>
            </div>
            {open && (
                <div className="mb-2 rounded-md border border-dashed border-border p-2">
                    <div className="grid grid-cols-3 gap-2">
                        <input className="akm-input text-[11px]" placeholder="€ amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        <input className="akm-input col-span-2 text-[11px]" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </div>
                    <div className="mt-2 flex justify-end">
                        <button className="akm-btn-primary text-[10px]" onClick={add}>Save</button>
                    </div>
                </div>
            )}
            {rows.length === 0 && <div className="text-[11px] text-muted-foreground">None</div>}
            {rows.map((r) => (
                <div key={r.id} className="flex items-baseline justify-between border-t border-border py-1 text-[11px]">
                    <div><b>-{money(r.amount)}</b> · {r.reason}</div>
                    <div className="text-[10px] text-muted-foreground">{r.type}</div>
                </div>
            ))}
        </div>
    );
}
function CreditsCard({ hotelId, rows, onChange }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const add = async () => {
        if (!amount || !reason) return toast.error("Amount + reason required");
        await api.post("/saas/credits", { hotel_id: hotelId, amount, reason });
        toast.success("Credit added"); setAmount(""); setReason(""); setOpen(false); onChange();
    };
    return (
        <div className="akm-card p-4">
            <div className="mb-3 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2"><Coins size={13} /> Credits</h3>
                <button className="akm-btn-primary text-[10px]" onClick={() => setOpen(!open)}>{open ? "Cancel" : <><Plus size={10} /> Add</>}</button>
            </div>
            {open && (
                <div className="mb-2 rounded-md border border-dashed border-border p-2">
                    <div className="grid grid-cols-3 gap-2">
                        <input className="akm-input text-[11px]" placeholder="€ amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        <input className="akm-input col-span-2 text-[11px]" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </div>
                    <div className="mt-2 flex justify-end">
                        <button className="akm-btn-primary text-[10px]" onClick={add}>Save</button>
                    </div>
                </div>
            )}
            {rows.length === 0 && <div className="text-[11px] text-muted-foreground">None</div>}
            {rows.map((r) => (
                <div key={r.id} className="flex items-baseline justify-between border-t border-border py-1 text-[11px]">
                    <div><b>+{money(r.amount)}</b> · {r.reason}</div>
                    <div className="text-[10px] text-muted-foreground">{r.kind}</div>
                </div>
            ))}
        </div>
    );
}
